import { useEffect } from "react";
import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import {
  ConsumerPageLayout,
  DefaultSeo,
  NotFoundPage,
  ScrollToTopOnNavigation,
  type GlobalSearchConfig,
} from "@appcaire/shared/seo";
import { trackPageView } from "./lib/analytics";
import { buildNackaProviderHref } from "./lib/providerRoutes";

// Page imports
import Home from "./pages/Home";
import HittaHemtjanst from "./pages/HittaHemtjanst";
import Ansoka from "./pages/Ansoka";
import Valja from "./pages/Valja";
import Byta from "./pages/Byta";
import Rattigheter from "./pages/Rattigheter";
import Utforare from "./pages/Utforare";
import Recensioner from "./pages/Recensioner";
import ProviderPage from "./pages/ProviderPage";
import ModernaSystemForUtforare from "./pages/ModernaSystemForUtforare";
import Kontakt from "./pages/Kontakt";
import HushallsnaraTjanster from "./pages/HushallsnaraTjanster";
import OmNackaHemtjanst from "./pages/OmNackaHemtjanst";
import VanligaFragor from "./pages/VanligaFragor";
import NewSocialServicesAct from "./pages/NewSocialServicesAct";
import BjornEvers from "./pages/shared/BjornEvers";
import SverkerCarlsson from "./pages/shared/SverkerCarlsson";

const navItems = [
  { label: "Hitta hemtjänst", to: "/hitta-hemtjanst" },
  { label: "Ansök om hjälp", to: "/ansoka" },
  { label: "Välja hemtjänst", to: "/valja" },
  { label: "Byta utförare", to: "/byta" },
  { label: "Dina rättigheter", to: "/rattigheter" },
  { label: "Anordnare", to: "/utforare" },
  { label: "Kontakt", to: "/kontakt" },
];

const searchConfig: GlobalSearchConfig = {
  enabledSources: ["providers", "articles"],
  municipalitySlug: "nacka",
  routes: {
    provider: buildNackaProviderHref,
  },
  articles: [
    {
      title: "Ansök om hemtjänst i Nacka",
      slug: "/ansoka",
      description: "Så ansöker du om stöd och får ett beslut.",
    },
    {
      title: "Välja hemtjänst i Nacka",
      slug: "/valja",
      description: "Tips för att välja rätt hemtjänstutförare.",
    },
    {
      title: "Byta hemtjänstutförare i Nacka",
      slug: "/byta",
      description: "Så byter du utförare via kundvalet.",
    },
    {
      title: "Dina rättigheter",
      slug: "/rattigheter",
      description: "Rättigheter, valfrihet och vad du kan påverka.",
    },
    {
      title: "Vanliga frågor",
      slug: "/vanliga-fragor-och-svar",
      description: "Svar på vanliga frågor om hemtjänst i Nacka.",
    },
    {
      title: "Nya socialtjänstlagen och hemtjänst i Nacka",
      slug: "/nya-socialtjanstlagen-hemtjanst",
      description:
        "Vad lagen betyder för ansökan, förebyggande stöd och hemtjänst i Nacka.",
    },
  ],
  copy: {
    placeholder: "Sök utförarenhet eller guide…",
    providersHeading: "Utförarenheter i Nacka",
    articlesHeading: "Guider",
  },
  allResultsCta: {
    label: "Visa alla utförarenheter",
    buildHref: (query) => `/utforare?q=${encodeURIComponent(query)}`,
  },
};

/**
 * Component to track page views on route changes
 * Only runs on client (in useEffect)
 */
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Only track on client side
    if (typeof window !== "undefined") {
      trackPageView(location.pathname, document.title);
    }
  }, [location]);

  return null;
};

/**
 * Main App component
 * Router is provided by entry-client.tsx (BrowserRouter) or entry-server.tsx (StaticRouter)
 */
function App() {
  const location = useLocation();

  return (
    <>
      <DefaultSeo siteKey="nackahemtjanst" />
      <PageViewTracker />
      <ScrollToTopOnNavigation />
      <ConsumerPageLayout
        brandName="NackaHemtjänst.se"
        brandNameShort="NackaHemtjänst"
        homeLink="/"
        navItems={navItems}
        siteKey="nackahemtjanst"
        searchConfig={searchConfig}
        location={location}
      >
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/hitta-hemtjanst" element={<HittaHemtjanst />} />
          <Route path="/ansoka" element={<Ansoka />} />
          <Route path="/valja" element={<Valja />} />
          <Route path="/byta" element={<Byta />} />
          <Route path="/rattigheter" element={<Rattigheter />} />
          <Route path="/utforare" element={<Utforare />} />
          <Route path="/jamfor" element={<Navigate to="/utforare" replace />} />
          <Route path="/recensioner" element={<Recensioner />} />
          <Route path="/anordnare/:providerSlug" element={<ProviderPage />} />
          <Route
            path="/utforare/moderna-system"
            element={<ModernaSystemForUtforare />}
          />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route
            path="/hushallsnara-tjanster"
            element={<HushallsnaraTjanster />}
          />
          <Route path="/om-nacka-hemtjanst" element={<OmNackaHemtjanst />} />
          <Route path="/vanliga-fragor-och-svar" element={<VanligaFragor />} />
          <Route
            path="/nya-socialtjanstlagen-hemtjanst"
            element={<NewSocialServicesAct />}
          />
          <Route path="om-oss/bjorn-evers" element={<BjornEvers />} />
          <Route path="om-oss/sverker-carlsson" element={<SverkerCarlsson />} />
          <Route
            path="*"
            element={
              <NotFoundPage
                showAttemptedPath={import.meta.env.DEV}
                navigationLinks={[
                  { label: "Tillbaka till startsidan", to: "/" },
                  { label: "Hitta hemtjänst", to: "/hitta-hemtjanst" },
                  { label: "Ansök om hjälp", to: "/ansoka" },
                  { label: "Välja hemtjänst", to: "/valja" },
                  { label: "Byta utförare", to: "/byta" },
                  { label: "Dina rättigheter", to: "/rattigheter" },
                  { label: "Anordnare", to: "/utforare" },
                  { label: "Kontakt", to: "/kontakt" },
                ]}
              />
            }
          />
        </Routes>
      </ConsumerPageLayout>
    </>
  );
}

export default App;
